import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from '@radix-ui/react-icons'

import type {
  DataTableInstance,
  DataTablePaginationProps,
  DataTableRowData,
} from 'blocks/cx-data-table'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'primitives/base/select'
import { Button } from 'primitives/button'

interface PaginationProps<TData extends DataTableRowData> extends DataTablePaginationProps {
  table: DataTableInstance<TData>
  pageSizeOptions?: number[]
}

export const DataTablePagination = <TData extends DataTableRowData>({
  table,
  pageSizeOptions = [10, 20, 30, 40, 50],
  isOneIndexed = false,
}: PaginationProps<TData>) => {
  const filteredRowModel = table.getFilteredRowModel()
  const {
    options: { showRowsPerPage },
  } = table
  const rowCount = table.options.manualPagination
    ? table.getRowCount()
    : filteredRowModel?.rows.length
  const noOfPages = Math.ceil(rowCount / table.getState().pagination.pageSize)

  // Get the internal 0-based index
  const internalPageIndex = table.getState().pagination.pageIndex
  // Convert to 1-based index for display if needed
  const displayPageIndex = isOneIndexed ? internalPageIndex + 1 : internalPageIndex
  const isLastPage = displayPageIndex === (isOneIndexed ? noOfPages : noOfPages - 1)

  return (
    <div className="flex w-full flex-col-reverse items-center justify-between gap-4 overflow-auto px-2 py-1 sm:flex-row sm:gap-8">
      <div className="flex flex-col-reverse items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8 justify-between flex-grow">
        {showRowsPerPage ? (
          <div className="flex items-center space-x-2">
            <p className="whitespace-nowrap font-inter text-s-400 normal-case tracking-normal">
              Rows per page
            </p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value))
              }}
            >
              <SelectTrigger className="h-8 w-[4.5rem] font-jetbrains text-xs-400 normal-case tracking-normal tabular-nums">
                <SelectValue placeholder={table.getState().pagination.pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {pageSizeOptions.map((pageSize) => (
                  <SelectItem
                    key={pageSize}
                    value={`${pageSize}`}
                    className="font-jetbrains text-xs-400 normal-case tracking-normal tabular-nums"
                  >
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="flex items-center justify-center font-jetbrains text-xs-400 normal-case tracking-normal tabular-nums text-secondary ml-auto">
          {displayPageIndex * table.getState().pagination.pageSize + 1}-
          {Math.min((displayPageIndex + 1) * table.getState().pagination.pageSize, rowCount)} of{' '}
          {rowCount}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            aria-label="Go to first page"
            variant="outline"
            className="hidden size-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <DoubleArrowLeftIcon className="size-4" aria-hidden="true" />
          </Button>
          <Button
            aria-label="Go to previous page"
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeftIcon className="size-4" aria-hidden="true" />
          </Button>
          <Button
            aria-label="Go to next page"
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => table.nextPage()}
            disabled={isLastPage}
          >
            <ChevronRightIcon className="size-4" aria-hidden="true" />
          </Button>
          <Button
            aria-label="Go to last page"
            variant="outline"
            size="icon"
            className="hidden size-8 lg:flex"
            onClick={() => table.setPageIndex(noOfPages - 1)}
            disabled={isLastPage}
          >
            <DoubleArrowRightIcon className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  )
}
